/**
 * Product renderer v2 — template clone, no infinite-scroll append.
 */

class CollectionProductRendererV2 {
  constructor(containerSelector) {
    this.container = typeof containerSelector === 'string'
      ? document.querySelector(containerSelector)
      : containerSelector;

    if (!this.container) {
      throw new Error('Product renderer container not found');
    }

    this.template = document.getElementById('product-card-template');
    if (!this.template) {
      throw new Error('Product card template not found');
    }

    this.newInDays = 7;
    this.skeletonCount = 8;
  }

  render(products) {
    if (!products || products.length === 0) {
      this.renderEmpty();
      return;
    }

    this.container.innerHTML = '';
    products.forEach(product => {
      this.container.appendChild(this.createProductCard(product));
    });
    this._syncFavouriteButtons();
  }

  restoreHtml(html) {
    if (!html) return;
    this.container.innerHTML = html;
    this._syncFavouriteButtons();
  }

  renderCarousel(trackElement, products) {
    if (!trackElement || !products?.length) return;

    const viewAllTile = trackElement.querySelector('[data-carousel-view-all]');
    trackElement.querySelectorAll('[data-carousel-skeleton], .product-carousel__item:not([data-carousel-view-all])').forEach((node) => {
      node.remove();
    });

    products.forEach(product => {
      const item = document.createElement('li');
      item.className = 'product-carousel__item shrink-0 snap-start';
      item.appendChild(this.createProductCard(product));
      if (viewAllTile) {
        trackElement.insertBefore(item, viewAllTile);
      } else {
        trackElement.appendChild(item);
      }
    });
    this._syncFavouriteButtons();
  }

  createProductCard(product) {
    const card = this.template.content.cloneNode(true);
    const isOnSale = product.compareAtPrice
      && parseFloat(product.compareAtPrice.amount) > parseFloat(product.price.amount);
    const isNewIn = this.isNewProduct(product.createdAt);

    const link = card.querySelector('[data-product-url]');
    if (link) link.href = product.url;

    const productId = product.shopifyProductId || product.id;
    const cardElement = card.querySelector('.product-card[data-product-id]');
    if (cardElement) {
      cardElement.dataset.productId = productId;
      if (product.handle) {
        cardElement.dataset.productHandle = product.handle;
      }
      if (product.shopifyProductId && product.id) {
        cardElement.dataset.productSku = product.id;
      }
      if (product.price?.amount != null) {
        cardElement.dataset.productPriceCents = Math.round(parseFloat(product.price.amount) * 100);
      }
    }

    const favouriteBtn = card.querySelector('.product-favourite-btn');
    if (favouriteBtn && productId) {
      favouriteBtn.dataset.productId = productId;
    }

    this.updateBadges(card, isNewIn, isOnSale, product);
    this.updateImage(card, product);

    const vendorElement = card.querySelector('[data-product-vendor]');
    if (vendorElement) vendorElement.textContent = product.vendor;

    const titleElement = card.querySelector('[data-product-title]');
    if (titleElement) titleElement.textContent = this.formatDisplayTitle(product.title);

    this.updateMetadata(card, product);
    this.updatePrice(card, product, isOnSale);
    this.updateSocialCount(card, product.favouritesCount);

    return card;
  }

  updateSocialCount(card, count) {
    const safeCount = Number.isFinite(Number(count)) ? Math.max(0, Number(count)) : 0;

    const wrapper = card.querySelector('[data-social-count]');
    if (wrapper) {
      const valueEl = wrapper.querySelector('.product-favourite-btn__social-count-value');
      const skeletonEl = wrapper.querySelector('.product-favourite-btn__social-count-skeleton');

      wrapper.classList.remove('is-social-count-ready', 'is-social-count-empty');

      if (safeCount > 0) {
        if (skeletonEl) skeletonEl.remove();
        if (valueEl) {
          valueEl.textContent = String(safeCount);
          valueEl.hidden = false;
          valueEl.removeAttribute('hidden');
        }
        wrapper.removeAttribute('aria-hidden');
        wrapper.classList.add('is-social-count-ready');
      } else {
        if (valueEl) {
          valueEl.hidden = true;
          valueEl.textContent = '';
        }
        wrapper.setAttribute('aria-hidden', 'true');
        wrapper.classList.add('is-social-count-empty');
      }
    }

    this.updateAttentionBanner(card, safeCount);
  }

  updateAttentionBanner(card, count) {
    const banner = card.querySelector('[data-attention-banner]');
    if (!banner) return;

    let tier = 'zero';
    if (count >= 20) {
      tier = 'popular';
    } else if (count >= 2) {
      tier = 'watching';
    }

    banner.dataset.tier = tier;

    banner.querySelectorAll('[data-attention-label]').forEach((label) => {
      label.hidden = label.dataset.attentionLabel !== tier;
    });

    const clockIcon = banner.querySelector('[data-attention-icon="clock"]');
    const eyeIcon = banner.querySelector('[data-attention-icon="eye"]');
    const fireIcon = banner.querySelector('[data-attention-icon="fire"]');

    [clockIcon, eyeIcon, fireIcon].forEach((icon) => {
      if (!icon) return;
      icon.hidden = true;
      icon.setAttribute('hidden', '');
    });

    const activeIcon = tier === 'zero' ? clockIcon : tier === 'watching' ? eyeIcon : fireIcon;
    if (activeIcon) {
      activeIcon.hidden = false;
      activeIcon.removeAttribute('hidden');
    }
  }

  _syncFavouriteButtons() {
    window.Favourites?.initializeButtons?.();
  }

  updateBadges(card, isNewIn, isOnSale, product) {
    const priceDrop = card.querySelector('[data-badge-price-drop]');
    const newBadge = card.querySelector('[data-badge-new]');

    if (isOnSale && priceDrop) {
      priceDrop.style.display = '';
      if (newBadge) newBadge.remove();
      const amountElement = priceDrop.querySelector('[data-price-drop-amount]');
      if (amountElement && product.compareAtPrice) {
        const dropAmount = parseFloat(product.compareAtPrice.amount) - parseFloat(product.price.amount);
        amountElement.textContent = `£${dropAmount.toFixed(2).replace('.00', '')} OFF`;
      }
    } else if (isNewIn && newBadge) {
      newBadge.style.display = '';
      if (priceDrop) priceDrop.remove();
    } else {
      if (priceDrop) priceDrop.remove();
      if (newBadge) newBadge.remove();
    }
  }

  buildImageUrl(baseUrl, size) {
    const url = new URL(baseUrl);
    url.searchParams.set('width', size);
    url.searchParams.set('height', size);
    url.searchParams.set('crop', 'center');
    return url.toString();
  }

  updateImage(card, product) {
    const imageContainer = card.querySelector('[data-image-container]');
    const imageElement = card.querySelector('[data-product-image]');
    const imageSizes = [200, 300, 400, 500, 600, 800];

    if (!imageContainer) return;

    const imageUrl = product.featuredImage?.url?.trim();

    if (imageUrl) {
      if (!imageElement) {
        console.warn('[CollectionProductRendererV2] Missing [data-product-image] in product card template');
        imageContainer.classList.add('product-card__figure--placeholder');
        return;
      }

      imageElement.src = this.buildImageUrl(imageUrl, 400);
      imageElement.srcset = imageSizes.map(size => `${this.buildImageUrl(imageUrl, size)} ${size}w`).join(', ');
      imageElement.sizes = '(min-width: 1024px) 320px, (min-width: 768px) 31vw, 46vw';
      imageElement.alt = product.featuredImage.altText || product.title;
      imageElement.width = 400;
      imageElement.height = 400;
      imageContainer.classList.remove('product-card__figure--placeholder');
    } else {
      imageElement?.remove();
      imageContainer.classList.add('product-card__figure--placeholder');
    }
  }

  updateMetadata(card, product) {
    const sizeElement = card.querySelector('[data-product-size]');
    const conditionElement = card.querySelector('[data-product-condition]');

    if (sizeElement) {
      if (product.sizeData?.value) {
        sizeElement.textContent = product.sizeData.value;
      } else {
        sizeElement.remove();
      }
    }

    if (conditionElement && product.condition?.value) {
      conditionElement.textContent = product.condition.value;
    }
  }

  updatePrice(card, product, isOnSale) {
    const saleWrapper = card.querySelector('[data-price-sale]');
    const regularWrapper = card.querySelector('[data-price-regular]');
    const formattedPrice = this.formatPrice(product.price.amount, product.price.currencyCode);

    if (isOnSale && product.compareAtPrice && saleWrapper && regularWrapper) {
      saleWrapper.style.display = 'flex';
      regularWrapper.style.display = 'none';
      const salePriceElement = saleWrapper.querySelector('[data-product-price]');
      const comparePriceElement = saleWrapper.querySelector('[data-product-compare-price]');
      if (salePriceElement) salePriceElement.textContent = formattedPrice;
      if (comparePriceElement) {
        comparePriceElement.textContent = `was ${this.formatPrice(
          product.compareAtPrice.amount,
          product.compareAtPrice.currencyCode
        )}`;
      }
    } else if (regularWrapper && saleWrapper) {
      regularWrapper.style.display = 'block';
      saleWrapper.style.display = 'none';
      const regularPriceElement = regularWrapper.querySelector('[data-product-price]');
      if (regularPriceElement) regularPriceElement.textContent = formattedPrice;
    }
  }

  formatDisplayTitle(title) {
    if (!title) return '';
    return title.split(' Size ')[0].trim();
  }

  formatPrice(amount, currencyCode) {
    try {
      const value = parseFloat(amount);
      const hasFraction = value % 1 !== 0;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: 2
      }).format(value);
    } catch (error) {
      return `${currencyCode} ${amount}`;
    }
  }

  isNewProduct(createdAt) {
    if (!createdAt) return false;
    try {
      const ageInDays = (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24);
      return ageInDays < this.newInDays;
    } catch (error) {
      return false;
    }
  }

  renderLoading(count = this.skeletonCount) {
    this.container.innerHTML = Array(count).fill(null).map(() => this.createSkeletonCard()).join('');
  }

  setLoadingState(isLoading) {
    this.container.classList.toggle('is-filter-loading', isLoading);
    this.container.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  createSkeletonCard() {
    return `
      <div class="block w-full rounded-[22px] border border-base-300 bg-base-100">
        <div class="relative aspect-square">
          <div class="h-full p-2">
            <div class="skeleton relative h-full w-full overflow-hidden rounded-xl"></div>
          </div>
        </div>
        <div class="min-w-0 px-2.5 pt-2.5 pb-3">
          <div class="skeleton h-3 w-20 rounded mb-2"></div>
          <div class="skeleton h-4 w-full rounded mb-1"></div>
          <div class="skeleton h-4 w-3/4 rounded mb-3"></div>
          <div class="skeleton h-5 w-16 rounded"></div>
        </div>
      </div>
    `;
  }

  renderEmpty(message = 'No products found') {
    this.container.innerHTML = '';
    const emptyDiv = document.createElement('div');
    emptyDiv.className = 'col-span-full py-12 text-center';
    const messageP = document.createElement('p');
    messageP.className = 'text-lg text-gray-500';
    messageP.textContent = message;
    emptyDiv.appendChild(messageP);
    this.container.appendChild(emptyDiv);
  }
}

if (typeof window !== 'undefined') {
  window.CollectionProductRendererV2 = CollectionProductRendererV2;
}
